import { useState, useEffect } from 'react';
import api from '../api';

/**
 * Whether this tenant's plan includes the Auction Calendar.
 *
 * =============================================================================
 * THE SERVER DECIDES. THIS ONLY ASKS.
 * =============================================================================
 * The verdict comes from GET /admin/calendar/access, which resolves it through
 * lib/calendarEntitlement, the same module that gates every calendar data
 * route. Nothing here reads a tier and works it out, and nothing here should
 * start to: a nav item that decides for itself will eventually disagree with
 * the route it links to, and a nav item that is wrong in the permissive
 * direction sends a Starter tenant to a page that 403s.
 *
 * =============================================================================
 * IT STARTS AT FALSE, AND `ready` IS WHY THAT IS SAFE
 * =============================================================================
 * `included` is false until the answer arrives, so a caller that ignores
 * `ready` hides the nav item for a moment and then shows it. That is the right
 * direction to be wrong in briefly.
 *
 * `ready` exists so a caller can tell "not entitled" from "not answered yet"
 * and avoid rendering an upgrade prompt during the gap, which would flash at
 * every Pro tenant on every page load.
 *
 * A FAILED REQUEST RESOLVES TO false, not to an error the caller must handle.
 * This drives whether one card is rendered. If the answer cannot be had, the
 * useful behaviour is a dashboard without that card, not a broken dashboard.
 */
export default function useCalendarEntitlement() {
  const [state, setState] = useState({ included: false, ready: false, upgrade: null });

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await api.get('/admin/calendar/access');
        if (cancelled) return;
        setState({
          included: data.calendarIncluded === true,
          ready: true,
          upgrade: data.upgrade || null
        });
      } catch {
        if (!cancelled) setState({ included: false, ready: true, upgrade: null });
      }
    })();

    return () => { cancelled = true; };
  }, []);

  return state;
}
